import { useState } from 'react';
import { Checkbox, InputNumber, Button, Modal, Input, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import './styles.scss';

interface TestRow {
  no: number;
  name: string;
  measures: string;
  time: string;
}

const TESTS: TestRow[] = [
  { no: 1, name: 'Cardiovascular (6MWT or Cooper Run)', measures: 'Cardiorespiratory endurance',      time: '6–12 min' },
  { no: 2, name: 'Push-Up Cadence Test',                 measures: 'Upper body muscular endurance',    time: '5 min'    },
  { no: 3, name: 'McGill Core Endurance Battery',        measures: 'Core stability & endurance',       time: '10 min'   },
  { no: 4, name: 'Active Knee Extension Test (AKE)',     measures: 'Hamstring flexibility',             time: '5 min'    },
  { no: 5, name: 'Back Scratch Test',                    measures: 'Shoulder / upper body mobility',   time: '3 min'    },
  { no: 6, name: 'Single Leg Stance Test (SLST)',        measures: 'Static balance & stability',       time: '4 min'    },
];

interface SummaryRowDef { no: number; name: string; categoryOptions: string[]; }

const SUMMARY_ROWS: SummaryRowDef[] = [
  { no: 1, name: 'Cardiovascular (6MWT / Cooper Run)', categoryOptions: ['Excellent', 'Good', 'Average', 'Poor'] },
  { no: 2, name: 'Push-Up Cadence',                    categoryOptions: ['Excellent', 'Good', 'Average', 'Poor'] },
  { no: 3, name: 'McGill Core Battery',                categoryOptions: ['Excellent', 'Good', 'Average', 'Below Average'] },
  { no: 4, name: 'AKE — Hamstring Flexibility',        categoryOptions: ['Excellent', 'Good', 'Average', 'Poor'] },
  { no: 5, name: 'Back Scratch — Shoulder',            categoryOptions: ['Excellent', 'Good', 'Average', 'Below Average'] },
  { no: 6, name: 'Single Leg Stance — Balance',        categoryOptions: ['Excellent', 'Good', 'Average', 'Poor'] },
];

interface SummaryRow { score: string; category: string | null; flag: boolean; }

const CHECKLIST_ITEMS = [
  'No strenuous exercise in last 24 hrs',
  'No caffeine on test day',
  'Heart rate monitor fitted (if used)',
  'Light meal only — no food 2 hrs before',
  'Comfortable clothing & shoes',
  'Informed client of all test procedures',
];

const INSTRUCTIONS_NOTE =
  'Conduct tests in order 1 to 6. Allow 3–5 minutes rest between tests. Ensure client is hydrated and warmed up with 5 minutes of easy walking before starting.';

const PREPARATION_ITEMS = [
  'Flat measured course — 50 m loop or 400 m track. Mark start/finish clearly.',
  'Stopwatch ready. Encourage client to wear comfortable walking shoes.',
  'Brief client: "Walk as fast as you can for 6 minutes. No jogging. Slow down if needed but do not stop."',
  'Do NOT walk alongside the client — it encourages them to match your pace.',
];

const PROTOCOL_STEPS = [
  { step: 1, title: 'Start.',     text: "Say 'Go' and start stopwatch simultaneously." },
  { step: 2, title: 'Encourage.', text: "Give standard encouragement every 60 seconds only: '1 minute done — keep it up!'" },
  { step: 3, title: 'Count laps.', text: 'Keep count of each completed lap.' },
  { step: 4, title: 'Call stop.', text: "At exactly 6:00 min call 'Stop'. Mark where client is standing immediately." },
  { step: 5, title: 'Measure.',   text: 'Measure remaining partial distance from last completed lap mark to where client stopped.' },
  { step: 6, title: 'Calculate.', text: 'Total = (Completed laps x lap length) + partial distance. Record below.' },
];

const CATEGORY_TABLE = [
  { category: 'Excellent',      distance: '> 700 m',   grade: 'excellent' },
  { category: 'Good',           distance: '600–700 m', grade: 'good'      },
  { category: 'Average',        distance: '500–599 m', grade: 'average'   },
  { category: 'Below Average',  distance: '< 500 m',   grade: 'below'     },
];

const AGE_NORMS = [
  { age: '20–39', men: '600–700 m', women: '550–650 m' },
  { age: '40–59', men: '550–650 m', women: '500–600 m' },
  { age: '60–74', men: '470–560 m', women: '430–520 m' },
  { age: '75+',   men: '350–450 m', women: '310–420 m' },
];

interface DistanceRecord {
  id: number;
  recordedAt: string;
  distance: number;
}

const gradeForDistance = (distance: number) => {
  if (distance > 700) return { label: 'Excellent', grade: 'excellent' };
  if (distance >= 600) return { label: 'Good', grade: 'good' };
  if (distance >= 500) return { label: 'Average', grade: 'average' };
  return { label: 'Below Average', grade: 'below' };
};

const PREPARATION_ITEMS_1B = [
  '400 m track preferred. Mark every 100 m with cones so client can track distance.',
  'Brief client: "Run as far as possible in 12 minutes. Pacing matters — start steady, not a sprint."',
  'Inform client of time at 4 min, 8 min, and 10 min so they can adjust pace.',
  'Walking is allowed but the full 12 minutes must be completed.',
];

const PROTOCOL_STEPS_1B = [
  { step: 1, title: 'Start.',      text: "Say 'Go' and start stopwatch simultaneously." },
  { step: 2, title: 'Call times.', text: "Announce '4 minutes done', '8 minutes done', '10 minutes — 2 minutes left'." },
  { step: 3, title: 'Count laps.', text: 'Keep count of each completed lap.' },
  { step: 4, title: 'Call stop.',  text: "At exactly 12:00 call 'Stop'. Place marker where client stops immediately." },
  { step: 5, title: 'Measure.',    text: 'Total = (Completed laps x lap length) + remaining metres from last full lap.' },
  { step: 6, title: 'Record.',     text: 'Enter total distance below and use table to find category.' },
];

const AGE_COLUMNS_1B = ['Age 20–29', 'Age 30–39', 'Age 40–49', 'Age 50–59', 'Age 60+'];

interface NormRow {
  category: string;
  grade: string;
  values: string[];
}

const MEN_NORMS_1B: NormRow[] = [
  { category: 'Excellent', grade: 'excellent', values: ['> 2800 m', '> 2700 m', '> 2500 m', '> 2400 m', '> 2100 m'] },
  { category: 'Good',      grade: 'good',      values: ['2400–2800', '2300–2700', '2100–2500', '1900–2400', '1700–2100'] },
  { category: 'Average',   grade: 'average',   values: ['2200–2399', '1900–2299', '1700–2099', '1500–1899', '1400–1699'] },
  { category: 'Poor',      grade: 'below',     values: ['< 2200', '< 1900', '< 1700', '< 1500', '< 1400'] },
];

const WOMEN_NORMS_1B: NormRow[] = [
  { category: 'Excellent', grade: 'excellent', values: ['> 2100 m', '> 1900 m', '> 1900 m', '> 1700 m', '> 1500 m'] },
  { category: 'Good',      grade: 'good',      values: ['1800–2100', '1700–1900', '1500–1900', '1400–1700', '1200–1500'] },
  { category: 'Average',   grade: 'average',   values: ['1500–1799', '1400–1699', '1200–1499', '1100–1399', '1000–1199'] },
  { category: 'Poor',      grade: 'below',     values: ['< 1500', '< 1400', '< 1200', '< 1100', '< 1000'] },
];

const PREPARATION_ITEMS_2 = [
  'Set metronome to 80 bpm. Each beep = one movement (down on beat 1, up on beat 2).',
  'Men: full push-up position — hands shoulder-width, body straight head to heel.',
  'Women: modified position — knees on floor, body straight from knees to head.',
  'Brief client: "Perform as many push-ups as you can at the metronome pace. I will stop you when form breaks."',
];

const PROTOCOL_STEPS_2 = [
  { step: 1, title: 'Check position.',  text: 'Verify hands shoulder-width apart. Hips not raised. Body alignment correct.' },
  { step: 2, title: 'Start metronome.', text: "Client begins on 'Go'. Count only reps with full range of motion." },
  { step: 3, title: 'Monitor form.',    text: 'Chest must come close to floor. Arms must fully extend at top.' },
  { step: 4, title: 'Stop test.',       text: 'Stop when client misses 2 consecutive beats OR loses correct form.' },
  { step: 5, title: 'Record.',          text: 'Total valid reps completed. Match to age and sex table below.' },
];

const MEN_PUSHUP_NORMS: NormRow[] = [
  { category: 'Excellent', grade: 'excellent', values: ['> 54', '> 44', '> 39', '> 34', '> 29'] },
  { category: 'Good',      grade: 'good',      values: ['45-54', '35-44', '30-39', '25-34', '20-29'] },
  { category: 'Average',   grade: 'average',   values: ['35-44', '24-34', '20-29', '15-24', '10-19'] },
  { category: 'Poor',      grade: 'below',     values: ['< 35', '< 24', '< 20', '< 15', '< 10'] },
];

const WOMEN_PUSHUP_NORMS: NormRow[] = [
  { category: 'Excellent', grade: 'excellent', values: ['> 48', '> 39', '> 34', '> 29', '> 19'] },
  { category: 'Good',      grade: 'good',      values: ['34-48', '25-39', '20-34', '15-29', '5-19'] },
  { category: 'Average',   grade: 'average',   values: ['17-33', '12-24', '8-19', '6-14', '3-4'] },
  { category: 'Poor',      grade: 'below',     values: ['< 17', '< 12', '< 8', '< 6', '< 3'] },
];

interface RepsRecord {
  id: number;
  recordedAt: string;
  reps: number;
}

const PREPARATION_ITEMS_3 = [
  'Stopwatch required. Gym mat for floor holds.',
  'Conduct 4 holds in order: Trunk Flexor → Trunk Extensor → Side Bridge R → Side Bridge L.',
  'Allow 2 minutes rest between each hold.',
  'Brief client: "Hold the position as long as you can. Tell me when you cannot maintain form."',
];

const PROTOCOL_STEPS_3 = [
  { step: 1, title: 'Trunk Flexor Hold.',    text: 'Client in sit-up position, back resting at 60 degrees on inclined support, arms crossed. Remove support — timer starts. Stop when back drops below 60 degrees.' },
  { step: 2, title: 'Rest 2 minutes.',       text: 'Allow client to rest fully before next hold.' },
  { step: 3, title: 'Trunk Extensor Hold.',  text: "Client lies prone on bench, upper body hanging off edge, pelvis secured with strap or trainer's hands. Client raises torso to horizontal and holds. Stop when hips drop." },
  { step: 4, title: 'Rest 2 minutes.',       text: 'Allow client to rest fully before next hold.' },
  { step: 5, title: 'Side Bridge — Right.',  text: 'Right side plank. Elbow under shoulder, feet stacked. Hips level with body. Stop when hips drop.' },
  { step: 6, title: 'Side Bridge — Left.',   text: 'Same on left side. Record time. Then calculate ratios below.' },
];

const CORE_NORM_TABLE_3 = [
  { test: 'Flexor',    men: '~144 sec', women: '~149 sec' },
  { test: 'Extensor',  men: '~146 sec', women: '~189 sec' },
  { test: 'Bridge R',  men: '~95 sec',  women: '~75 sec'  },
  { test: 'Bridge L',  men: '~99 sec',  women: '~72 sec'  },
];

const RATIO_CHECK_TABLE_3 = [
  { ratio: 'Flexion / Extension',    range: '< 1.0 (extension must be dominant)' },
  { ratio: 'R Bridge / L Bridge',    range: '0.95 – 1.05 (sides near equal)'     },
  { ratio: 'Side Bridge / Extension', range: '0.40 – 0.75'                       },
];

interface CoreHoldRecord {
  id: number;
  recordedAt: string;
  flexor: number;
  extensor: number;
  bridgeR: number;
  bridgeL: number;
  flexExtRatio: number;
  rlRatio: number;
  bridgeExtRatio: number;
  overallCategory: string;
}

const PREPARATION_ITEMS_4 = [
  'Client lies flat on back (supine) on a gym mat.',
  'Goniometer preferred for measurement. If unavailable, visual estimation and a plumb line works.',
  'The test measures the DEFICIT (degrees short of full extension). Lower angle = better flexibility.',
  'Brief client: "I will support your thigh vertically. Straighten your knee as much as you comfortably can."',
];

const PROTOCOL_STEPS_4 = [
  { step: 1, title: 'Position client.', text: 'Client lies supine. Flex one hip to 90 degrees — thigh vertical, perpendicular to floor.' },
  { step: 2, title: 'Stabilise hip.',   text: 'Trainer holds the thigh firmly at 90 degrees. Client hugs thigh with both hands to help stabilise.' },
  { step: 3, title: 'Extend knee.',     text: 'Client actively straightens the knee as far as possible. Hold for 2 seconds.' },
  { step: 4, title: 'Measure angle.',   text: 'Measure angle remaining between lower leg and full extension (0 degrees = perfect). This is the KNEE FLEXION DEFICIT. Record in degrees.' },
  { step: 5, title: 'Switch legs.',     text: 'Repeat on the other leg. Compare both sides — note any asymmetry > 10 degrees.' },
];

const CATEGORY_TABLE_4 = [
  { category: 'Excellent (Optimal)',      deficit: '0 – 10 degrees',  meaning: 'Full hamstring length. No restriction.',                     grade: 'excellent' },
  { category: 'Good',                     deficit: '11 – 20 degrees', meaning: 'Minor tightness. Acceptable for most activities.',           grade: 'good'      },
  { category: 'Average (Mild Tightness)', deficit: '21 – 30 degrees', meaning: 'Hamstrings moderately tight. Common in sedentary adults.',    grade: 'average'   },
  { category: 'Below Average (Tight)',    deficit: '31 – 45 degrees', meaning: 'Significant tightness. Corrective stretching needed.',       grade: 'below'     },
  { category: 'Poor (Very Tight)',        deficit: '45+ degrees',     meaning: 'Severe hamstring restriction. Refer for assessment.',         grade: 'poor'      },
];

const NOTE_4 = 'Normal cut-off for active adults: 20 degrees or less. A side-to-side difference greater than 10 degrees is clinically significant — flag it even if absolute scores are good. Source: Gabbe et al. (2004) — British Journal of Sports Medicine.';

interface KneeExtensionRecord {
  id: number;
  recordedAt: string;
  rightDeficit: number;
  leftDeficit: number;
  asymmetry: number;
  flagged: boolean;
}

const PREPARATION_ITEMS_5 = [
  'Client stands upright. Shoes remain on. Ensure adequate space around client.',
  'Ruler or tape measure needed for gap/overlap measurement.',
  'Positive score = fingertips overlap (good). Negative score = gap between fingertips (restricted).',
  'Brief client: "Reach one hand over your shoulder down your back, and the other up your back from below."',
];

const PROTOCOL_STEPS_5 = [
  { step: 1, title: 'Right dominant.',    text: 'Right hand over RIGHT shoulder, palm facing back, reaching DOWN. Left hand behind back, palm facing out, reaching UP.' },
  { step: 2, title: 'Measure.',           text: 'Measure distance between tips of middle fingers. Overlap = positive (+). Gap = negative (–).' },
  { step: 3, title: 'Left dominant.',     text: 'Reverse: Left hand over LEFT shoulder reaching down. Right hand reaching up.' },
  { step: 4, title: 'Measure again.',     text: 'Record left side score.' },
  { step: 5, title: 'Note dominant side.', text: 'Record which side performs better. Flag if difference > 5 cm between sides.' },
];

const CATEGORY_TABLE_5 = [
  { category: 'Excellent',     criterion: 'Overlap > 5 cm',       grade: 'excellent' },
  { category: 'Good',          criterion: 'Touch or overlap 0–5 cm', grade: 'good'    },
  { category: 'Average',       criterion: 'Gap 0–5 cm apart',     grade: 'average'    },
  { category: 'Below Average', criterion: 'Gap > 5 cm apart',     grade: 'below'      },
];

const AGE_NORM_TABLE_5 = [
  { age: '60–64', men: '-6.6 to -0.2 cm', women: '-3.0 to +1.6 cm' },
  { age: '65–69', men: '-7.0 to -1.0 cm', women: '-3.5 to +1.0 cm' },
  { age: '70–74', men: '-2.0 to +0.5 cm', women: '0.0 to +1.0 cm'  },
  { age: '75–79', men: '-2.5 to +0.8 cm', women: '-1.5 to +0.8 cm' },
];

const NOTE_5 = 'Age norms (right table) are only validated for adults aged 60+. For members under 60 use the CATEGORY table only.';

interface BackScratchRecord {
  id: number;
  recordedAt: string;
  rightScore: number;
  leftScore: number;
  difference: number;
  flagged: boolean;
}

const PREPARATION_ITEMS_6 = [
  'Client stands barefoot on a flat, non-slip surface. Remove shoes.',
  'Trainer stands close for safety — do NOT touch unless client is about to fall.',
  'Stopwatch required. Run eyes-open test first, then eyes-closed.',
  'Cap at 30 seconds eyes open / 10 seconds eyes closed. Record best of 2 trials per leg per condition.',
  'Brief client: "Stand on one leg with hands on hips. Hold as still as possible until I say stop."',
];

const PROTOCOL_STEPS_6 = [
  { step: 1, title: 'Position.',        text: 'Client stands, hands on hips, eyes looking forward at a fixed point on the wall.' },
  { step: 2, title: 'Lift foot.',       text: 'Client lifts one foot — timer starts the moment the foot leaves the floor.' },
  { step: 3, title: 'Stop timer when:', text: 'Lifted foot touches ground / Feet touch each other / Hands leave hips / Excessive trunk sway.' },
  { step: 4, title: 'Record.',          text: 'Best of 2 trials for each leg. Then repeat EYES CLOSED — same rules apply.' },
  { step: 5, title: 'Both legs.',       text: 'Test Right leg first, then Left. Compare — asymmetry > 5 seconds is significant.' },
];

const EYES_OPEN_COLUMNS_6 = ['18–39', '40–49', '50–59', '60–69', '70+'];

const EYES_OPEN_TABLE_6 = [
  { category: 'Excellent', grade: 'excellent', values: ['30 sec (max)', '28–30 sec', '24–29 sec', '18–25 sec', '10–18 sec'] },
  { category: 'Good',      grade: 'good',      values: ['25–29 sec', '22–27 sec', '15–23 sec', '10–17 sec', '5–9 sec'] },
  { category: 'Average',   grade: 'average',   values: ['15–24 sec', '12–21 sec', '8–14 sec', '4–9 sec', '2–4 sec'] },
  { category: 'Poor',      grade: 'poor',      values: ['< 15 sec', '< 12 sec', '< 8 sec', '< 4 sec', '< 2 sec'] },
];

const EYES_CLOSED_COLUMNS_6 = ['18–39', '40–59', '60+'];

const EYES_CLOSED_TABLE_6 = [
  { category: 'Good',    values: ['> 15 sec', '> 8 sec', '> 4 sec'] },
  { category: 'Average', values: ['8–15 sec', '4–8 sec', '2–4 sec'] },
  { category: 'Flag',    values: ['< 8 sec', '< 4 sec', '< 2 sec'] },
];

const NOTE_6 = 'Source: Springer B.A. et al. (2007). Normative values for the unipedal stance test. J Geriatric Physical Therapy, 30(1):8-15. | Clinical flag: inability to hold single leg stance for 5+ seconds in older adults = increased fall risk. | Side-to-side difference > 5 seconds = flag regardless of absolute score.';

interface StanceRecord {
  id: number;
  recordedAt: string;
  eoRight: number;
  eoLeft: number;
  ecRight: number;
  ecLeft: number;
  eoAsymmetry: number;
  ecAsymmetry: number;
  flagged: boolean;
}

const Assessment = () => {
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [showInfo, setShowInfo] = useState(false);
  const [showTest1Info, setShowTest1Info] = useState(false);
  const [records, setRecords] = useState<DistanceRecord[]>([]);
  const [distanceModalOpen, setDistanceModalOpen] = useState(false);
  const [coveredDistance, setCoveredDistance] = useState<number | null>(null);

  const [showTest1bInfo, setShowTest1bInfo] = useState(false);
  const [records1b, setRecords1b] = useState<DistanceRecord[]>([]);
  const [distance1bModalOpen, setDistance1bModalOpen] = useState(false);
  const [coveredDistance1b, setCoveredDistance1b] = useState<number | null>(null);

  const [showTest2Info, setShowTest2Info] = useState(false);
  const [records2, setRecords2] = useState<RepsRecord[]>([]);
  const [reps2ModalOpen, setReps2ModalOpen] = useState(false);
  const [repsCompleted, setRepsCompleted] = useState<number | null>(null);

  const [showTest3Info, setShowTest3Info] = useState(false);
  const [records3, setRecords3] = useState<CoreHoldRecord[]>([]);
  const [core3ModalOpen, setCore3ModalOpen] = useState(false);
  const [flexorSec, setFlexorSec] = useState<number | null>(null);
  const [extensorSec, setExtensorSec] = useState<number | null>(null);
  const [bridgeRSec, setBridgeRSec] = useState<number | null>(null);
  const [bridgeLSec, setBridgeLSec] = useState<number | null>(null);

  const [showTest4Info, setShowTest4Info] = useState(false);
  const [records4, setRecords4] = useState<KneeExtensionRecord[]>([]);
  const [knee4ModalOpen, setKnee4ModalOpen] = useState(false);
  const [rightDeficit, setRightDeficit] = useState<number | null>(null);
  const [leftDeficit, setLeftDeficit] = useState<number | null>(null);

  const [showTest5Info, setShowTest5Info] = useState(false);
  const [records5, setRecords5] = useState<BackScratchRecord[]>([]);
  const [scratch5ModalOpen, setScratch5ModalOpen] = useState(false);
  const [rightScore, setRightScore] = useState<number | null>(null);
  const [leftScore, setLeftScore] = useState<number | null>(null);

  const [showTest6Info, setShowTest6Info] = useState(false);
  const [records6, setRecords6] = useState<StanceRecord[]>([]);
  const [stance6ModalOpen, setStance6ModalOpen] = useState(false);
  const [eoRight, setEoRight] = useState<number | null>(null);
  const [eoLeft, setEoLeft] = useState<number | null>(null);
  const [ecRight, setEcRight] = useState<number | null>(null);
  const [ecLeft, setEcLeft] = useState<number | null>(null);

  const [summaryMeta, setSummaryMeta] = useState({ client: '', date: '', trainer: '' });
  const [summaryRows, setSummaryRows] = useState<SummaryRow[]>(
    SUMMARY_ROWS.map(() => ({ score: '', category: null, flag: false }))
  );
  const [trainerNotes, setTrainerNotes] = useState({
    keyFindings: '', strengths: '', priorityAreas: '', recommendations: '', nextAssessment: '',
  });
  const [signatures, setSignatures] = useState({ trainerSignature: '', clientSignature: '', date: '' });

  const updateSummaryRow = (index: number, patch: Partial<SummaryRow>) =>
    setSummaryRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const toggleItem = (i: number) =>
    setChecked(prev => ({ ...prev, [i]: !prev[i] }));

  const handleSaveDistance = () => {
    if (coveredDistance == null) return;
    setRecords(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        distance: coveredDistance,
      },
    ]);
    setCoveredDistance(null);
    setDistanceModalOpen(false);
  };

  const handleSaveDistance1b = () => {
    if (coveredDistance1b == null) return;
    setRecords1b(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        distance: coveredDistance1b,
      },
    ]);
    setCoveredDistance1b(null);
    setDistance1bModalOpen(false);
  };

  const handleSaveReps2 = () => {
    if (repsCompleted == null) return;
    setRecords2(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        reps: repsCompleted,
      },
    ]);
    setRepsCompleted(null);
    setReps2ModalOpen(false);
  };

  const handleSaveCore3 = () => {
    if (flexorSec == null || extensorSec == null || bridgeRSec == null || bridgeLSec == null) return;
    const flexExtRatio   = extensorSec > 0 ? flexorSec / extensorSec : 0;
    const rlRatio         = bridgeLSec > 0 ? bridgeRSec / bridgeLSec : 0;
    const bridgeExtRatio = extensorSec > 0 ? ((bridgeRSec + bridgeLSec) / 2) / extensorSec : 0;
    const inRange = flexExtRatio < 1.0 && rlRatio >= 0.95 && rlRatio <= 1.05 && bridgeExtRatio >= 0.40 && bridgeExtRatio <= 0.75;
    setRecords3(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        flexor: flexorSec,
        extensor: extensorSec,
        bridgeR: bridgeRSec,
        bridgeL: bridgeLSec,
        flexExtRatio,
        rlRatio,
        bridgeExtRatio,
        overallCategory: inRange ? 'Balanced' : 'Imbalance Detected',
      },
    ]);
    setFlexorSec(null);
    setExtensorSec(null);
    setBridgeRSec(null);
    setBridgeLSec(null);
    setCore3ModalOpen(false);
  };

  const handleSaveKnee4 = () => {
    if (rightDeficit == null || leftDeficit == null) return;
    const asymmetry = Math.abs(rightDeficit - leftDeficit);
    setRecords4(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        rightDeficit,
        leftDeficit,
        asymmetry,
        flagged: asymmetry > 10,
      },
    ]);
    setRightDeficit(null);
    setLeftDeficit(null);
    setKnee4ModalOpen(false);
  };

  const handleSaveScratch5 = () => {
    if (rightScore == null || leftScore == null) return;
    const difference = Math.abs(rightScore - leftScore);
    setRecords5(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        rightScore,
        leftScore,
        difference,
        flagged: difference > 5,
      },
    ]);
    setRightScore(null);
    setLeftScore(null);
    setScratch5ModalOpen(false);
  };

  const handleSaveStance6 = () => {
    if (eoRight == null || eoLeft == null || ecRight == null || ecLeft == null) return;
    const eoAsymmetry = Math.abs(eoRight - eoLeft);
    const ecAsymmetry = Math.abs(ecRight - ecLeft);
    setRecords6(prev => [
      ...prev,
      {
        id: Date.now(),
        recordedAt: new Date().toLocaleString(),
        eoRight,
        eoLeft,
        ecRight,
        ecLeft,
        eoAsymmetry,
        ecAsymmetry,
        flagged: eoAsymmetry > 5 || ecAsymmetry > 5,
      },
    ]);
    setEoRight(null);
    setEoLeft(null);
    setEcRight(null);
    setEcLeft(null);
    setStance6ModalOpen(false);
  };

  return (
    <div className="user-assessment-worksheet">
      <div className="uaw-banner">
        <h2>Fitclub Performance Lab</h2>
        <p>Fitness Assessment Worksheet &middot; Trainer Record Sheet &middot; 6-Test Battery</p>
      </div>

      <div className="uaw-section">
        <div className="uaw-section-header">
          <span>Today's Assessment — 6 Tests</span>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowInfo(prev => !prev)}
          />
        </div>
        {showInfo && <div className="uaw-note">{INSTRUCTIONS_NOTE}</div>}
        <div className="uaw-table">
          <div className="uaw-table-row uaw-table-head">
            <span className="col-no">#</span>
            <span className="col-name">Test Name</span>
            <span className="col-measures">Measures</span>
            <span className="col-time">Est. Time</span>
          </div>
          {TESTS.map(t => (
            <div className="uaw-table-row" key={t.no}>
              <span className="col-no">{t.no}</span>
              <span className="col-name">{t.name}</span>
              <span className="col-measures">{t.measures}</span>
              <span className="col-time">{t.time}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="uaw-section">
        <div className="uaw-section-header">Pre-Test Checklist</div>
        <div className="uaw-checklist">
          {CHECKLIST_ITEMS.map((item, i) => (
            <Checkbox key={i} checked={!!checked[i]} onChange={() => toggleItem(i)}>
              {item}
            </Checkbox>
          ))}
        </div>
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 1 — Cardiovascular Endurance</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest1Info(prev => !prev)}
          />
        </div>
        <p className="uaw-subtitle">
          Choose Option A (general / deconditioned) or Option B (fit / active members)
        </p>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">1A</span>
          <div>
            <div className="uaw-option-title">6-Minute Walk Test (6MWT)</div>
            <div className="uaw-option-source">Source: ATS, 2002 &middot; Walking only — no jogging or running at any point</div>
          </div>
        </div>

        {showTest1Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-subheader">Distance Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setDistanceModalOpen(true)}>Add Distance</Button>
        </div>

        <Modal
          title="Covered Distance"
          open={distanceModalOpen}
          onOk={handleSaveDistance}
          onCancel={() => { setDistanceModalOpen(false); setCoveredDistance(null); }}
          okText="Save"
          okButtonProps={{ disabled: coveredDistance == null }}
        >
          <div className="uaw-field">
            <label>Covered Distance (m)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={coveredDistance}
              onChange={(v) => setCoveredDistance(v as number | null)}
              placeholder="Enter distance in meters"
              autoFocus
            />
          </div>
        </Modal>

        {records.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Distance (m)</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => {
                  const g = gradeForDistance(r.distance);
                  return (
                    <tr key={r.id} className={`grade-${g.grade}`}>
                      <td>{r.recordedAt}</td>
                      <td>{r.distance}</td>
                      <td>{g.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        <div className="uaw-norms-grid">
          <table className="uaw-norm-table">
            <thead><tr><th>Category</th><th>Distance</th></tr></thead>
            <tbody>
              {CATEGORY_TABLE.map(c => {
                const latest = records[records.length - 1];
                const isCurrent = latest ? gradeForDistance(latest.distance).grade === c.grade : false;
                return (
                  <tr key={c.category} className={`grade-${c.grade}${isCurrent ? ' uaw-row-current' : ''}`}>
                    <td>{c.category}</td><td>{c.distance}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <table className="uaw-norm-table">
            <thead><tr><th>Age</th><th>Men (norm)</th><th>Women (norm)</th></tr></thead>
            <tbody>
              {AGE_NORMS.map(a => (
                <tr key={a.age}><td>{a.age}</td><td>{a.men}</td><td>{a.women}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 1 — Cardiovascular Endurance (Continued)</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest1bInfo(prev => !prev)}
          />
        </div>
        <p className="uaw-subtitle">
          Option B — For fit / active members who can run
        </p>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">1B</span>
          <div>
            <div className="uaw-option-title">Cooper 12-Minute Run Test</div>
            <div className="uaw-option-source">Source: Cooper, K.H. (1968). JAMA, 203(3):135-138 &middot; ACSM endorsed</div>
          </div>
        </div>

        {showTest1bInfo && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_1B.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_1B.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-subheader">Distance Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setDistance1bModalOpen(true)}>Add Distance</Button>
        </div>

        <Modal
          title="Covered Distance"
          open={distance1bModalOpen}
          onOk={handleSaveDistance1b}
          onCancel={() => { setDistance1bModalOpen(false); setCoveredDistance1b(null); }}
          okText="Save"
          okButtonProps={{ disabled: coveredDistance1b == null }}
        >
          <div className="uaw-field">
            <label>Covered Distance (m)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={coveredDistance1b}
              onChange={(v) => setCoveredDistance1b(v as number | null)}
              placeholder="Enter distance in meters"
              autoFocus
            />
          </div>
        </Modal>

        {records1b.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Distance (m)</th>
                </tr>
              </thead>
              <tbody>
                {records1b.map(r => (
                  <tr key={r.id}>
                    <td>{r.recordedAt}</td>
                    <td>{r.distance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="uaw-subheader">Men — Distance Norms (metres)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {AGE_COLUMNS_1B.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {MEN_NORMS_1B.map(row => (
              <tr key={row.category} className={`grade-${row.grade}`}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="uaw-subheader">Women — Distance Norms (metres)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {AGE_COLUMNS_1B.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {WOMEN_NORMS_1B.map(row => (
              <tr key={row.category} className={`grade-${row.grade}`}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 2 — Push-Up Cadence Test</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest2Info(prev => !prev)}
          />
        </div>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">2</span>
          <div>
            <div className="uaw-option-title">Push-Up Cadence Test</div>
            <div className="uaw-option-source">Muscular Endurance &middot; CASS, 1987 / ACSM Guidelines</div>
          </div>
        </div>

        {showTest2Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_2.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_2.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-subheader">Reps Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setReps2ModalOpen(true)}>Add Reps</Button>
        </div>

        <Modal
          title="Reps Completed"
          open={reps2ModalOpen}
          onOk={handleSaveReps2}
          onCancel={() => { setReps2ModalOpen(false); setRepsCompleted(null); }}
          okText="Save"
          okButtonProps={{ disabled: repsCompleted == null }}
        >
          <div className="uaw-field">
            <label>Reps Completed</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={repsCompleted}
              onChange={(v) => setRepsCompleted(v as number | null)}
              placeholder="Enter number of reps"
              autoFocus
            />
          </div>
        </Modal>

        {records2.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Reps</th>
                </tr>
              </thead>
              <tbody>
                {records2.map(r => (
                  <tr key={r.id}>
                    <td>{r.recordedAt}</td>
                    <td>{r.reps}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="uaw-subheader">Men — Full Push-Up Norms (repetitions)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {AGE_COLUMNS_1B.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {MEN_PUSHUP_NORMS.map(row => (
              <tr key={row.category} className={`grade-${row.grade}`}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="uaw-subheader">Women — Modified Knee Push-Up Norms (repetitions)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {AGE_COLUMNS_1B.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {WOMEN_PUSHUP_NORMS.map(row => (
              <tr key={row.category} className={`grade-${row.grade}`}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 3 — McGill Core Endurance Battery</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest3Info(prev => !prev)}
          />
        </div>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">3</span>
          <div>
            <div className="uaw-option-title">McGill Core Endurance Battery</div>
            <div className="uaw-option-source">Core Stability &middot; McGill, Childs &amp; Lieberson — Arch. PM&amp;R., 1999</div>
          </div>
        </div>

        {showTest3Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_3.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_3.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-norms-grid">
          <table className="uaw-norm-table">
            <thead><tr><th>Test</th><th>Men (norm 20-40)</th><th>Women (norm 20-40)</th></tr></thead>
            <tbody>
              {CORE_NORM_TABLE_3.map(r => (
                <tr key={r.test}><td>{r.test}</td><td>{r.men}</td><td>{r.women}</td></tr>
              ))}
            </tbody>
          </table>
          <table className="uaw-norm-table">
            <thead><tr><th>Ratio Check</th><th>Target Range</th></tr></thead>
            <tbody>
              {RATIO_CHECK_TABLE_3.map(r => (
                <tr key={r.ratio}><td>{r.ratio}</td><td>{r.range}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="uaw-subheader">Time Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setCore3ModalOpen(true)}>Add Times</Button>
        </div>

        <Modal
          title="Core Hold Times"
          open={core3ModalOpen}
          onOk={handleSaveCore3}
          onCancel={() => {
            setCore3ModalOpen(false);
            setFlexorSec(null); setExtensorSec(null); setBridgeRSec(null); setBridgeLSec(null);
          }}
          okText="Save"
          okButtonProps={{ disabled: flexorSec == null || extensorSec == null || bridgeRSec == null || bridgeLSec == null }}
        >
          <div className="uaw-field">
            <label>Flexor (sec)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={flexorSec} onChange={(v) => setFlexorSec(v as number | null)} placeholder="Enter seconds" autoFocus />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Extensor (sec)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={extensorSec} onChange={(v) => setExtensorSec(v as number | null)} placeholder="Enter seconds" />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Side Bridge R (sec)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={bridgeRSec} onChange={(v) => setBridgeRSec(v as number | null)} placeholder="Enter seconds" />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Side Bridge L (sec)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={bridgeLSec} onChange={(v) => setBridgeLSec(v as number | null)} placeholder="Enter seconds" />
          </div>
        </Modal>

        {records3.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Flexor</th>
                  <th>Extensor</th>
                  <th>Bridge R</th>
                  <th>Bridge L</th>
                  <th>Flex/Ext</th>
                  <th>R/L</th>
                  <th>Bridge/Ext</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {records3.map(r => (
                  <tr key={r.id} className={r.overallCategory === 'Balanced' ? 'grade-excellent' : 'grade-below'}>
                    <td>{r.recordedAt}</td>
                    <td>{r.flexor}s</td>
                    <td>{r.extensor}s</td>
                    <td>{r.bridgeR}s</td>
                    <td>{r.bridgeL}s</td>
                    <td>{r.flexExtRatio.toFixed(2)}</td>
                    <td>{r.rlRatio.toFixed(2)}</td>
                    <td>{r.bridgeExtRatio.toFixed(2)}</td>
                    <td>{r.overallCategory}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 4 — Active Knee Extension Test (AKE)</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest4Info(prev => !prev)}
          />
        </div>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">4</span>
          <div>
            <div className="uaw-option-title">Active Knee Extension Test (AKE)</div>
            <div className="uaw-option-source">Hamstring Flexibility &middot; Gold standard — isolates hamstring better than Sit-and-Reach</div>
          </div>
        </div>

        {showTest4Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_4.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_4.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <table className="uaw-norm-table uaw-age-norm-table">
          <thead><tr><th>Category</th><th>Knee Flexion Deficit</th><th>What It Means</th></tr></thead>
          <tbody>
            {CATEGORY_TABLE_4.map(c => (
              <tr key={c.category} className={`grade-${c.grade}`}>
                <td>{c.category}</td>
                <td>{c.deficit}</td>
                <td>{c.meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="uaw-note">{NOTE_4}</div>

        <div className="uaw-subheader">Deficit Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setKnee4ModalOpen(true)}>Add Measurement</Button>
        </div>

        <Modal
          title="Knee Extension Deficit"
          open={knee4ModalOpen}
          onOk={handleSaveKnee4}
          onCancel={() => { setKnee4ModalOpen(false); setRightDeficit(null); setLeftDeficit(null); }}
          okText="Save"
          okButtonProps={{ disabled: rightDeficit == null || leftDeficit == null }}
        >
          <div className="uaw-field">
            <label>Right Leg Deficit (degrees)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={rightDeficit} onChange={(v) => setRightDeficit(v as number | null)} placeholder="Enter degrees" autoFocus />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Left Leg Deficit (degrees)</label>
            <InputNumber min={0} style={{ width: '100%' }} value={leftDeficit} onChange={(v) => setLeftDeficit(v as number | null)} placeholder="Enter degrees" />
          </div>
        </Modal>

        {records4.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Right Leg Deficit</th>
                  <th>Left Leg Deficit</th>
                  <th>Asymmetry (R vs L)</th>
                </tr>
              </thead>
              <tbody>
                {records4.map(r => (
                  <tr key={r.id} className={r.flagged ? 'grade-below' : undefined}>
                    <td>{r.recordedAt}</td>
                    <td>{r.rightDeficit}&deg;</td>
                    <td>{r.leftDeficit}&deg;</td>
                    <td>{r.asymmetry}&deg;{r.flagged ? ' ⚠ Flagged' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 5 — Back Scratch Test</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest5Info(prev => !prev)}
          />
        </div>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">5</span>
          <div>
            <div className="uaw-option-title">Back Scratch Test</div>
            <div className="uaw-option-source">Upper Body / Shoulder Mobility &middot; Rikli &amp; Jones — Senior Fitness Test, 1999</div>
          </div>
        </div>

        {showTest5Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_5.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_5.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-norms-grid">
          <table className="uaw-norm-table">
            <thead><tr><th>Category</th><th>Criterion</th></tr></thead>
            <tbody>
              {CATEGORY_TABLE_5.map(c => (
                <tr key={c.category} className={`grade-${c.grade}`}>
                  <td>{c.category}</td><td>{c.criterion}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="uaw-norm-table">
            <thead><tr><th>Age (60+)</th><th>Men — Normal</th><th>Women — Normal</th></tr></thead>
            <tbody>
              {AGE_NORM_TABLE_5.map(a => (
                <tr key={a.age}><td>{a.age}</td><td>{a.men}</td><td>{a.women}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="uaw-note-warning">{NOTE_5}</div>

        <div className="uaw-subheader">Score Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setScratch5ModalOpen(true)}>Add Scores</Button>
        </div>

        <Modal
          title="Back Scratch Scores"
          open={scratch5ModalOpen}
          onOk={handleSaveScratch5}
          onCancel={() => { setScratch5ModalOpen(false); setRightScore(null); setLeftScore(null); }}
          okText="Save"
          okButtonProps={{ disabled: rightScore == null || leftScore == null }}
        >
          <div className="uaw-field">
            <label>Right Dominant Score (cm)</label>
            <InputNumber style={{ width: '100%' }} value={rightScore} onChange={(v) => setRightScore(v as number | null)} placeholder="Overlap +, Gap −" autoFocus />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Left Dominant Score (cm)</label>
            <InputNumber style={{ width: '100%' }} value={leftScore} onChange={(v) => setLeftScore(v as number | null)} placeholder="Overlap +, Gap −" />
          </div>
        </Modal>

        {records5.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>Right Dominant</th>
                  <th>Left Dominant</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {records5.map(r => (
                  <tr key={r.id} className={r.flagged ? 'grade-below' : undefined}>
                    <td>{r.recordedAt}</td>
                    <td>{r.rightScore} cm</td>
                    <td>{r.leftScore} cm</td>
                    <td>{r.difference} cm{r.flagged ? ' ⚠ Flagged' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Test 6 — Single Leg Stance Test (SLST)</h2>
          <InfoCircleOutlined
            className="uaw-info-icon"
            onClick={() => setShowTest6Info(prev => !prev)}
          />
        </div>

        <div className="uaw-option-card">
          <span className="uaw-option-badge">6</span>
          <div>
            <div className="uaw-option-title">Single Leg Stance Test (SLST)</div>
            <div className="uaw-option-source">Static Balance &middot; Springer et al. (2007), J Geriatric Physical Therapy &middot; No equipment needed</div>
          </div>
        </div>

        {showTest6Info && (
          <>
            <div className="uaw-subheader">Preparation</div>
            <ul className="uaw-prep-list">
              {PREPARATION_ITEMS_6.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <div className="uaw-subheader">Step-by-Step Protocol</div>
            <div className="uaw-steps">
              {PROTOCOL_STEPS_6.map(s => (
                <div className="uaw-step-row" key={s.step}>
                  <span className="uaw-step-num">{s.step}</span>
                  <span className="uaw-step-text"><strong>{s.title}</strong> {s.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="uaw-subheader">Eyes Open — Norms (cap 30 sec)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {EYES_OPEN_COLUMNS_6.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {EYES_OPEN_TABLE_6.map(row => (
              <tr key={row.category} className={`grade-${row.grade}`}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="uaw-subheader">Eyes Closed — Norms (cap 10 sec)</div>
        <table className="uaw-norm-table uaw-age-norm-table">
          <thead>
            <tr>
              <th>Category</th>
              {EYES_CLOSED_COLUMNS_6.map(a => <th key={a}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {EYES_CLOSED_TABLE_6.map(row => (
              <tr key={row.category}>
                <td>{row.category}</td>
                {row.values.map((v, i) => <td key={i}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="uaw-note">{NOTE_6}</div>

        <div className="uaw-subheader">Stance Time Measurement</div>
        <div className="uaw-form-actions">
          <Button type="primary" onClick={() => setStance6ModalOpen(true)}>Add Times</Button>
        </div>

        <Modal
          title="Single Leg Stance Times"
          open={stance6ModalOpen}
          onOk={handleSaveStance6}
          onCancel={() => { setStance6ModalOpen(false); setEoRight(null); setEoLeft(null); setEcRight(null); setEcLeft(null); }}
          okText="Save"
          okButtonProps={{ disabled: eoRight == null || eoLeft == null || ecRight == null || ecLeft == null }}
        >
          <div className="uaw-field">
            <label>Eyes Open — Right (sec, cap 30)</label>
            <InputNumber style={{ width: '100%' }} value={eoRight} onChange={(v) => setEoRight(v as number | null)} placeholder="seconds" autoFocus />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Eyes Open — Left (sec, cap 30)</label>
            <InputNumber style={{ width: '100%' }} value={eoLeft} onChange={(v) => setEoLeft(v as number | null)} placeholder="seconds" />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Eyes Closed — Right (sec, cap 10)</label>
            <InputNumber style={{ width: '100%' }} value={ecRight} onChange={(v) => setEcRight(v as number | null)} placeholder="seconds" />
          </div>
          <div className="uaw-field" style={{ marginTop: 12 }}>
            <label>Eyes Closed — Left (sec, cap 10)</label>
            <InputNumber style={{ width: '100%' }} value={ecLeft} onChange={(v) => setEcLeft(v as number | null)} placeholder="seconds" />
          </div>
        </Modal>

        {records6.length > 0 && (
          <>
            <div className="uaw-subheader">Recorded Results</div>
            <table className="uaw-norm-table uaw-results-table">
              <thead>
                <tr>
                  <th>Recorded At</th>
                  <th>EO Right</th>
                  <th>EO Left</th>
                  <th>EC Right</th>
                  <th>EC Left</th>
                  <th>Asymmetry</th>
                </tr>
              </thead>
              <tbody>
                {records6.map(r => (
                  <tr key={r.id} className={r.flagged ? 'grade-below' : undefined}>
                    <td>{r.recordedAt}</td>
                    <td>{r.eoRight}s</td>
                    <td>{r.eoLeft}s</td>
                    <td>{r.ecRight}s</td>
                    <td>{r.ecLeft}s</td>
                    <td>EO {r.eoAsymmetry}s / EC {r.ecAsymmetry}s{r.flagged ? ' ⚠ Flagged' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div className="uaw-section">
        <div className="uaw-banner uaw-test-banner">
          <h2>Fitness Assessment Summary</h2>
        </div>

        <div className="uaw-summary-meta">
          <div className="uaw-field">
            <label>Client</label>
            <Input value={summaryMeta.client} onChange={(e) => setSummaryMeta(prev => ({ ...prev, client: e.target.value }))} placeholder="Client name" />
          </div>
          <div className="uaw-field">
            <label>Date</label>
            <Input value={summaryMeta.date} onChange={(e) => setSummaryMeta(prev => ({ ...prev, date: e.target.value }))} placeholder="DD-MM-YYYY" />
          </div>
          <div className="uaw-field">
            <label>Trainer</label>
            <Input value={summaryMeta.trainer} onChange={(e) => setSummaryMeta(prev => ({ ...prev, trainer: e.target.value }))} placeholder="Trainer name" />
          </div>
        </div>

        <div className="uaw-section-header">Results at a Glance</div>
        <div className="uaw-summary-table">
          <div className="uaw-summary-row uaw-summary-head">
            <span>Test</span>
            <span>Score</span>
            <span>Category</span>
            <span>Flag</span>
          </div>
          {SUMMARY_ROWS.map((row, i) => (
            <div className="uaw-summary-row" key={row.no}>
              <span className="uaw-summary-test">{row.no}. {row.name}</span>
              <Input
                className="uaw-summary-score"
                placeholder="Score"
                value={summaryRows[i].score}
                onChange={(e) => updateSummaryRow(i, { score: e.target.value })}
              />
              <Select
                className="uaw-summary-category"
                placeholder="Select"
                allowClear
                value={summaryRows[i].category}
                onChange={(v) => updateSummaryRow(i, { category: v ?? null })}
                options={row.categoryOptions.map(c => ({ label: c, value: c }))}
              />
              <Checkbox
                className="uaw-summary-flag"
                checked={summaryRows[i].flag}
                onChange={(e) => updateSummaryRow(i, { flag: e.target.checked })}
              />
            </div>
          ))}
        </div>

        <div className="uaw-section-header">Trainer Notes</div>
        <div className="uaw-summary-notes">
          <div className="uaw-field">
            <label>Key Findings / Observations</label>
            <Input.TextArea rows={2} value={trainerNotes.keyFindings} onChange={(e) => setTrainerNotes(prev => ({ ...prev, keyFindings: e.target.value }))} />
          </div>
          <div className="uaw-field">
            <label>Client Strengths</label>
            <Input.TextArea rows={2} value={trainerNotes.strengths} onChange={(e) => setTrainerNotes(prev => ({ ...prev, strengths: e.target.value }))} />
          </div>
          <div className="uaw-field">
            <label>Priority Areas for Improvement</label>
            <Input.TextArea rows={2} value={trainerNotes.priorityAreas} onChange={(e) => setTrainerNotes(prev => ({ ...prev, priorityAreas: e.target.value }))} />
          </div>
          <div className="uaw-field">
            <label>Programme Recommendations</label>
            <Input.TextArea rows={2} value={trainerNotes.recommendations} onChange={(e) => setTrainerNotes(prev => ({ ...prev, recommendations: e.target.value }))} />
          </div>
          <div className="uaw-field">
            <label>Next Assessment Date</label>
            <Input value={trainerNotes.nextAssessment} onChange={(e) => setTrainerNotes(prev => ({ ...prev, nextAssessment: e.target.value }))} placeholder="DD-MM-YYYY" />
          </div>
        </div>

        <div className="uaw-summary-signatures">
          <div className="uaw-field">
            <label>Trainer Signature</label>
            <Input value={signatures.trainerSignature} onChange={(e) => setSignatures(prev => ({ ...prev, trainerSignature: e.target.value }))} placeholder="Trainer name" />
          </div>
          <div className="uaw-field">
            <label>Client Signature</label>
            <Input value={signatures.clientSignature} onChange={(e) => setSignatures(prev => ({ ...prev, clientSignature: e.target.value }))} placeholder="Client name" />
          </div>
          <div className="uaw-field">
            <label>Date</label>
            <Input value={signatures.date} onChange={(e) => setSignatures(prev => ({ ...prev, date: e.target.value }))} placeholder="DD-MM-YYYY" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
